import * as React from 'react';

import { createReactRenderer } from 'helpers/react-renderer';

import DiscountRuleDropdown from '../DiscountRuleDropdown';
import { flashWarning, getEmployeeShopsIDs } from '../utils';
import { fetchShops as employeeShopsFetchShops } from '../../EmployeeShops/utils/shops';

interface Props {
  className?: string;
  initialShops?: AutocompleteOption[];
  isDirtyOnChange?: boolean;
  isDisabled?: boolean;
  limitToShopID: number;
  name: string;
  selectAllOption?: boolean;
  selectAllText?: string;
  selectAllValue?: number;
}

const DiscountRuleShops: React.FC<Props> = ({
  className = '',
  initialShops = [],
  isDirtyOnChange,
  isDisabled,
  limitToShopID,
  name,
  selectAllOption = true,
  selectAllText = 'All locations',
  selectAllValue,
}) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [shops, setShops] = React.useState<AutocompleteOption[]>([]);

  React.useEffect(() => {
    const fetchShops = async () => {
      const hasMEREModule = window.lsCloud.Modules.hasModule(
        'settings.employees.limit_to_multiple_shops'
      );
      const isPrimaryUser = await window.lsCloud.Config.access('is_primary_user');

      try {
        // use fetchShops in EmployeeShops utils
        const shops = await employeeShopsFetchShops();
        // map object from "Shop" to "AutocompleteOption"
        const shopsList = shops.map(
          ({ name, id }): AutocompleteOption => ({
            label: name,
            value: id || '',
          })
        );
        let employeeShops: AutocompleteOption[] = [];

        // Before MERE
        if (!hasMEREModule) {
          if (isPrimaryUser || !limitToShopID) {
            employeeShops = shopsList;
          } else {
            const restrictedShop = shopsList.find(shop => shop.value === limitToShopID);
            if (restrictedShop) {
              employeeShops = [restrictedShop];
            }
          }
        }

        // After MERE
        else {
          const employeeShopsResponse = await window.merchantos.session.apiRequest(
            `API/Account/{{systemCustomerID}}/Employee/${
              window.merchantos?.session?.getEmployee()?.employeeID
            }.json?load_relations=["EmployeeShops"]`
          );
          const employeeShopsIDs = getEmployeeShopsIDs(employeeShopsResponse);
          // @ts-expect-error ts-migrate(2339) FIXME: Property 'includes' does not exist on type 'never'... Remove this comment to see the full error message
          employeeShops = shopsList.filter(shop => employeeShopsIDs.includes(shop.value));
        }

        setShops(employeeShops);
      } catch (error) {
        flashWarning('Locations fetching failed', error);
      }

      setIsLoading(false);
    };

    fetchShops();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DiscountRuleDropdown
      className={className}
      initialOptions={initialShops}
      isDirtyOnChange={isDirtyOnChange}
      isDisabled={isDisabled}
      isLoading={isLoading}
      isMulti={true}
      name={name}
      options={shops}
      placeholder="Select one or more locations"
      selectAllOption={selectAllOption}
      selectAllText={selectAllText}
      selectAllValue={selectAllValue}
    />
  );
};

const RenderedComponent = createReactRenderer({
  component: DiscountRuleShops,
  selector: '[data-react-root="DiscountRuleShops"]',
});

window.history.onChangeState(
  {
    id: undefined,
    name: 'discount_rule.views.discount_rule',
  },
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);

export default DiscountRuleShops;
